import { useResource } from "@tenergy/web-core/dashboard";
import { adminReads } from "../api/admin-api.js";
import type { PoolCheck, PoolsNetwork, PoolView, ProviderPools } from "../api/types.js";
import { count, utc } from "../format.js";
import type { Router } from "../router.js";
import { Address, ErrorNote, Note, Panel, ScreenHeader, Spinner, Table, Td, Th } from "../ui.js";

/**
 * Provider pools (mock docs/product/mocks/admin-pool-state-mock.html, approved by the owner 2026-10-27): how much
 * energy each provider can give out now and how fast it comes back, per tracked pool, from the analyzer's microtron
 * (computed on every block by the node's rules and checked against the node). `/pools` lists the providers,
 * `/pools/<provider>` is one provider. ACCESS: viewer, as Network (owner 2026-10-28).
 * The mock's staker sections (wallets with the provider's key, stakers delegating into a pool) have no data yet and
 * are not shown.
 */

const USDT_TRANSFER = 65_000;
const M = (n: number, digits = 2) => `${(n / 1e6).toFixed(digits)} M`;
const big = (n: number) => (Math.abs(n) >= 1e6 ? M(n) : count(Math.round(n)));
const hours = (h: number) => (h > 0 ? `${h.toFixed(1)} h` : "—");

export function PoolsScreen({ router, provider }: { router: Router; provider: string | null }) {
  return provider === null ? <PoolList router={router} /> : <ProviderPage router={router} provider={provider} />;
}

function NetworkStrip({ net, asOf }: { net: PoolsNetwork; asOf: string }) {
  return (
    <div class="mb-4 flex flex-wrap gap-x-6 gap-y-2 rounded-[16px] border border-line bg-surface px-4 py-2.5 text-[12px] text-ink-muted">
      <span>
        Block <b class="num text-ink">{count(net.block)}</b> · lag <b class="num text-ink">{net.lag_seconds} s</b>
      </span>
      <span>
        Network weight <b class="num text-ink">{(net.energy_weight_trx / 1e9).toFixed(2)} B TRX</b>
      </span>
      <span>
        Energy per TRX <b class="num text-ink">{net.energy_per_trx.toFixed(3)}</b>
      </span>
      <span>
        USDT transfer ({count(USDT_TRANSFER)} energy) = <b class="num text-ink">{count(net.usdt_transfer_stake_trx)} TRX</b> of stake
      </span>
      <span>
        Weights vs node, 24 h: <CheckText check={net.check} />
      </span>
      <span>as of {utc(asOf)}</span>
    </div>
  );
}

function CheckText({ check }: { check: PoolCheck | null }) {
  if (check === null) return <b class="text-ink-muted">no checks yet</b>;
  const tone = check.last_ok ? "text-save" : "text-warning";
  return (
    <b class={`num ${tone}`}>
      {check.exact} of {check.checks} exact
      {check.last_ok ? "" : ` · last off by ${count(check.last_debt_diff)}`}
    </b>
  );
}

const LEGEND: ReadonlyArray<[string, string]> = [
  ["var(--color-ink-muted)", "delegated to customers"],
  ["#1f6f55", "own stake — held by debt"],
  ["var(--color-save)", "own stake — can delegate"],
  ["#2e3466", "received from stakers — used"],
  ["var(--color-accent)", "received from stakers — free"],
];

function Bar({ parts }: { parts: Array<[number, string]> }) {
  const total = parts.reduce((s, [v]) => s + Math.max(0, v), 0);
  return (
    <div class="flex h-3.5 overflow-hidden rounded-[5px] bg-surface-raised">
      {total <= 0
        ? null
        : parts.map(([v, color], i) => <span key={i} style={{ width: `${(Math.max(0, v) / total) * 100}%`, background: color }} />)}
    </div>
  );
}

/** The pool's energy split as the node charges it: debt against received energy first, then against own stake. */
function energySplit(p: PoolView) {
  const stake = p.own_energy_trx + p.acquired_energy_trx + p.v1_energy_trx;
  const perTrx = stake > 0 ? p.energy_limit / stake : 0;
  const received = (p.acquired_energy_trx + p.v1_energy_trx) * perTrx;
  const own = p.own_energy_trx * perTrx;
  const recvUsed = Math.min(p.energy_used, received);
  const ownUsed = Math.min(own, p.energy_used - recvUsed);
  return { ownUsed, ownFree: own - ownUsed, recvFree: received - recvUsed, recvUsed };
}

function PoolBlock({ p }: { p: PoolView }) {
  const e = energySplit(p);
  const ownDebtTrx = Math.max(0, p.own_energy_trx - p.can_delegate_trx);
  return (
    <div class="mt-1 border-t border-line pt-2.5">
      <div class="mb-1.5 flex flex-wrap justify-between gap-x-3 gap-y-1 text-[12px]">
        <span>
          Pool <Address value={p.address} />
        </span>
        <span class="num text-ink-muted">
          own stake {big(p.own_energy_trx + p.delegated_out_energy_trx)} TRX · received {big(p.acquired_energy_trx + p.v1_energy_trx)} TRX
        </span>
      </div>
      <div class="my-1 grid items-center gap-2.5 sm:grid-cols-[130px_1fr]">
        <span class="text-[11px] text-ink-muted">Own stake, TRX</span>
        <Bar
          parts={[
            [p.delegated_out_energy_trx, "var(--color-ink-muted)"],
            [ownDebtTrx, "#1f6f55"],
            [p.can_delegate_trx, "var(--color-save)"],
          ]}
        />
      </div>
      <div class="my-1 grid items-center gap-2.5 sm:grid-cols-[130px_1fr]">
        <span class="text-[11px] text-ink-muted">Pool energy</span>
        <Bar
          parts={[
            [e.ownUsed, "#1f6f55"],
            [e.ownFree, "var(--color-save)"],
            [e.recvFree, "var(--color-accent)"],
            [e.recvUsed, "#2e3466"],
          ]}
        />
      </div>
      <div class="flex flex-wrap gap-x-3.5 gap-y-0.5 text-[11px] text-ink-muted sm:ml-[140px]">
        <span>
          limit <b class="num text-ink">{big(p.energy_limit)}</b>
        </span>
        <span>
          debt <b class="num text-ink">{big(p.energy_used)}</b>
        </span>
        <span>
          free <b class="num text-ink">{big(p.energy_free)}</b>
        </span>
        <span>
          node check, 24 h: <CheckText check={p.check} />
        </span>
        {p.corridor_open ? (
          <span>
            <b class="num text-warning">a 100k+ TRX reclaim awaits the node: free at least {big(p.energy_free_min)}</b>
          </span>
        ) : null}
        <span>data at {utc(p.at)}</span>
      </div>
    </div>
  );
}

function Kpi({ label, value, unit, note, main = false, warn = false }: { label: string; value: string; unit?: string; note?: string; main?: boolean; warn?: boolean }) {
  return (
    <div class="min-w-0 rounded-[12px] bg-surface-raised px-2.5 py-2">
      <div class="num text-[10px] tracking-[0.05em] text-ink-muted uppercase">{label}</div>
      <div class={`num text-[17px] leading-tight font-semibold ${main ? "text-save" : "text-ink"}`}>
        {value}
        {unit === undefined ? null : <small class="ml-1 text-[11px] font-normal text-ink-muted">{unit}</small>}
      </div>
      {note === undefined ? null : <div class={`text-[11px] ${warn ? "text-warning" : "text-ink-muted"}`}>{note}</div>}
    </div>
  );
}

function ProviderCard({ group, router }: { group: ProviderPools; router: Router }) {
  const t = group.totals;
  const window = Math.max(0, ...group.pools.map((p) => p.window_hours));
  const perHour = group.pools.reduce((s, p) => s + p.recovery_per_hour, 0);
  const received = t.acquired_energy_trx > 0;
  const ownAll = t.own_energy_trx + t.delegated_out_energy_trx;
  const href = `/pools/${encodeURIComponent(group.provider)}`;
  return (
    <section class="mb-3 rounded-[20px] border border-line bg-surface px-4 py-3.5">
      <div class="mb-2.5 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <h2 class="font-display text-[16px] font-extrabold">
          <a href={href} onClick={(event) => router.onNavigate(event as MouseEvent, "pools", group.provider)} class="border-b border-dashed border-ink-muted/40">
            {group.provider}
          </a>
        </h2>
        <span class="text-[11.5px] text-ink-muted">{group.pools.length === 1 ? "1 pool tracked" : `${group.pools.length} pools tracked`}</span>
      </div>
      <div class="mb-3 grid grid-cols-2 gap-2 lg:grid-cols-5">
        <Kpi
          main
          label="Can delegate now"
          value={M(t.can_delegate_energy)}
          unit="energy"
          note={`${big(t.can_delegate_trx)} TRX · ≈ ${count(Math.floor(t.can_delegate_energy / USDT_TRANSFER))} transfers`}
        />
        <Kpi label="In 1 hour" value={M(t.can_delegate_energy_1h)} note={`+${M(t.can_delegate_energy_1h - t.can_delegate_energy)} an hour`} />
        <Kpi
          label="Recovery window"
          value={hours(window)}
          {...(window > 0 ? { unit: `×${(24 / window).toFixed(2)}` } : {})}
          note={window > 0 ? `debt ${M(t.energy_used)} gone in ${hours(window)} · ${M(perHour)} an hour` : "no debt"}
        />
        {received ? (
          <Kpi
            label="Free in the pool"
            value={M(t.energy_free)}
            note={t.energy_free_min < t.energy_free ? `at least ${M(t.energy_free_min)}` : `${M(t.energy_free_1h - t.energy_free)} more in 1 h`}
            warn={t.energy_free_min < t.energy_free}
          />
        ) : (
          <Kpi
            label="Delegated to customers"
            value={big(t.delegated_out_energy_trx)}
            unit="TRX"
            note={ownAll > 0 ? `${Math.round((t.delegated_out_energy_trx / ownAll) * 100)} % of own stake` : ""}
          />
        )}
        <Kpi label="Bandwidth" value={big(t.bandwidth_free)} unit="free" note={`can delegate ${big(t.bandwidth_can_delegate_trx)} TRX`} />
      </div>
      {group.pools.map((p) => (
        <PoolBlock key={p.address} p={p} />
      ))}
    </section>
  );
}

function PoolList({ router }: { router: Router }) {
  const res = useResource((signal) => {
    void signal;
    return adminReads.pools();
  }, []);
  const view = res.data;
  return (
    <>
      <ScreenHeader
        title="Provider pools"
        lead="How much energy each provider can give out right now and how fast it comes back. Computed on every block by the node's rules and checked against the node. Sorted by what can be delegated now."
      />
      {view == null ? (
        res.error !== null ? <ErrorNote error={res.error} onRetry={() => res.reload()} /> : <Spinner />
      ) : (
        <>
          {Object.keys(view.errors).length > 0 ? <Note tone="warning">{Object.entries(view.errors).map(([k, v]) => `${k}: ${v}`).join(" · ")}</Note> : null}
          {view.network === null ? null : <NetworkStrip net={view.network} asOf={view.as_of} />}
          <div class="mb-3 flex flex-wrap gap-x-4 gap-y-1.5 text-[11.5px] text-ink-muted">
            {LEGEND.map(([color, label]) => (
              <span key={label}>
                <i class="mr-1.5 inline-block size-2.5 rounded-[3px] align-[-1px]" style={{ background: color }} />
                {label}
              </span>
            ))}
          </div>
          {view.providers.length === 0 ? (
            <Note>No pool data from the analyzer yet: microtron writes it once it has loaded the pools (about 15 minutes after the collector starts).</Note>
          ) : (
            view.providers.map((g) => <ProviderCard key={g.provider} group={g} router={router} />)
          )}
          <p class="max-w-3xl text-[11.5px] text-ink-muted">
            "Can delegate" is the pool's own stake only: energy received from stakers the pool spends itself, the node does not let it be passed on.
            Debt is charged to received energy first, then to own stake (the node's rule). "At least" is the free energy with a large reclaim not yet
            confirmed by the node: the true figure lies between that and the one shown.
          </p>
        </>
      )}
    </>
  );
}

function ProviderPage({ router, provider }: { router: Router; provider: string }) {
  const res = useResource(
    (signal) => {
      void signal;
      return adminReads.providerPools(provider);
    },
    [provider],
  );
  const view = res.data;
  const back = (
    <a href="/pools" onClick={(event) => router.onNavigate(event as MouseEvent, "pools")} class="num text-[11px] text-accent-text hover:underline">
      ← All providers
    </a>
  );
  if (view == null) return res.error !== null ? <ErrorNote error={res.error} onRetry={() => res.reload()} /> : <Spinner />;
  const top = Math.max(1, ...view.history.map((h) => h.free));
  return (
    <>
      {back}
      <ScreenHeader title={view.provider} lead={`The provider's tracked pools. Data at ${utc(view.pools[0]?.at ?? view.as_of)}, block ${count(view.pools[0]?.block ?? 0)}.`} />
      {view.network === null ? null : <NetworkStrip net={view.network} asOf={view.as_of} />}
      <div class="grid grid-cols-1 gap-3.5">
        <Panel title="Pools" subtitle="Wallets others delegate into. Energy in energy units, stake in TRX." flush>
          <Table
            rows={view.pools.length}
            empty="No tracked pools."
            head={
              <tr>
                <Th>Pool</Th>
                <Th numeric>Own stake</Th>
                <Th numeric>Delegated to customers</Th>
                <Th numeric>Received</Th>
                <Th numeric>Limit</Th>
                <Th numeric>Debt</Th>
                <Th numeric>Free</Th>
                <Th numeric>Window</Th>
                <Th numeric>Recovery</Th>
                <Th numeric>Can delegate</Th>
                <Th numeric>Node check, 24 h</Th>
              </tr>
            }
          >
            {view.pools.map((p) => (
              <tr key={p.address}>
                <Td mono>
                  <Address value={p.address} />
                </Td>
                <Td numeric>{count(Math.round(p.own_energy_trx))}</Td>
                <Td numeric>{count(Math.round(p.delegated_out_energy_trx))}</Td>
                <Td numeric>{count(Math.round(p.acquired_energy_trx + p.v1_energy_trx))}</Td>
                <Td numeric>{count(p.energy_limit)}</Td>
                <Td numeric>{count(p.energy_used)}</Td>
                <Td numeric>
                  {count(p.energy_free)}
                  {p.energy_free_min < p.energy_free ? <span class="block text-[11px] text-warning">at least {count(p.energy_free_min)}</span> : null}
                </Td>
                <Td numeric>
                  {hours(p.window_hours)}
                  {p.window_hours > 0 ? <span class="block text-[11px] text-ink-muted">×{(24 / p.window_hours).toFixed(2)}</span> : null}
                </Td>
                <Td numeric>{p.recovery_per_hour > 0 ? `${M(p.recovery_per_hour)}/h` : "—"}</Td>
                <Td numeric>
                  <span class="text-save">{count(Math.round(p.can_delegate_trx))} TRX</span>
                  <span class="block text-[11px] text-ink-muted">{big(p.can_delegate_energy)} energy</span>
                </Td>
                <Td numeric>
                  <CheckText check={p.check} />
                  {p.check === null ? null : <span class="block text-[11px] text-ink-muted">max off {count(p.check.max_abs_diff)} · last {utc(p.check.last_at)}</span>}
                </Td>
              </tr>
            ))}
          </Table>
        </Panel>
        <Panel title="Free in the pool in N minutes" subtitle="The node's rule: debt falls linearly to the end of its window. Without new spends or reclaims.">
          <div class="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
            {view.forecast.map((f) => (
              <Kpi key={f.minutes} label={f.minutes === 0 ? "Now" : f.minutes < 60 ? `${f.minutes} min` : `${(f.minutes / 60).toFixed(f.minutes % 60 === 0 ? 0 : 1)} h`} value={M(f.free, 1)} />
            ))}
          </div>
        </Panel>
        <Panel title="Free in the pool, last 24 hours" subtitle="At the end of each hour, from the pool's recorded states.">
          {view.history_error !== null ? (
            <Note tone="warning">{view.history_error}</Note>
          ) : view.history.length === 0 ? (
            <Note>No hourly states yet.</Note>
          ) : (
            <>
              <div class="grid h-20 items-end gap-[3px]" style={{ gridTemplateColumns: `repeat(${view.history.length}, minmax(0, 1fr))` }}>
                {view.history.map((h) => (
                  <i key={h.hour} title={`${utc(h.hour)}: ${M(h.free)}`} class="block rounded-t-[3px] border-t-2 border-save bg-save/15" style={{ height: `${(h.free / top) * 100}%` }} />
                ))}
              </div>
              <div class="num mt-1.5 flex justify-between text-[10px] text-ink-muted">
                <span>{utc(view.history[0]!.hour)}</span>
                <span>now</span>
              </div>
            </>
          )}
        </Panel>
      </div>
    </>
  );
}
